import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, setSelectedPlayerId, Player, addKeyframe, setCurrentTime } from './store';
import { Stage, Layer, Rect, Circle, RegularPolygon, Text, Line, Group, Image as KonvaImage } from 'react-konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { getInterpolatedEntities, lerp } from './utils';
import { triggerExportEvent } from './UI';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as HME from 'h264-mp4-encoder';

const COURT_WIDTH = 900;
const COURT_HEIGHT = 600;

const BackgroundImage: React.FC<{ url: string }> = ({ url }) => {
    const [image, setImage] = useState<HTMLImageElement | null>(null);

    useEffect(() => {
        if (!url) {
            setImage(null);
            return;
        }
        const img = new window.Image();
        img.crossOrigin = 'Anonymous'; // This is the critical fix
        img.onload = () => setImage(img);
        img.src = url;
    }, [url]);

    if (!image) return <Group />;

    const scale = Math.min(COURT_WIDTH / image.width, COURT_HEIGHT / image.height);
    const finalWidth = image.width * scale;
    const finalHeight = image.height * scale;
    const offsetX = (COURT_WIDTH - finalWidth) / 2;
    const offsetY = (COURT_HEIGHT - finalHeight) / 2;

    return <KonvaImage image={image} x={offsetX} y={offsetY} width={finalWidth} height={finalHeight} />;
};

export const CanvasBoard: React.FC = () => {
    const dispatch = useDispatch();
    const { currentTime, players, keyframes, backgroundImageUrl: bgUrl, selectedPlayerId, isPlaying } = useSelector((state: RootState) => state.app);

    const stageRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [stageScale, setStageScale] = useState(1);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            if (containerRef.current) {
                // Determine padding to keep some white space (e.g., 16px)
                const containerWidth = containerRef.current.clientWidth - 16;
                const containerHeight = containerRef.current.clientHeight - 16;
                const scale = Math.min(containerWidth / COURT_WIDTH, containerHeight / COURT_HEIGHT);
                setStageScale(scale > 0 ? scale : 1);
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const playerRadius = isMobile ? 15 * 2 : 15;
    const ballRadius = isMobile ? 8 * 2.5 : 8;

    const interpolatedEntities = useMemo(() => {
        return getInterpolatedEntities(keyframes, currentTime);
    }, [keyframes, currentTime]);

    const livePlayers = useMemo(() => {
        return players.map(p => {
            const coords = interpolatedEntities[p.id] || { x: 100, y: 100 };
            return { ...p, x: coords.x, y: coords.y };
        });
    }, [players, interpolatedEntities]);

    const liveBall = useMemo(() => {
        return interpolatedEntities['ball'] || { x: 450, y: 300, attachedTo: null };
    }, [interpolatedEntities]);

    // Override visual positions immediately on dragging so it feels magnetic/instant without lagging dispatcher checks
    const [dragOverwrites, setDragOverwrites] = useState<{ id: string, x: number, y: number }[]>([]);
    const [ballOverwrite, setBallOverwrite] = useState<{ x: number, y: number, attachedTo: string | null } | null>(null);

    useEffect(() => {
        setDragOverwrites([]);
        setBallOverwrite(null);
    }, [currentTime]);

    const getCurrentFrameState = () => {
        const framePlayers = livePlayers.map(p => {
            const dragOv = dragOverwrites.find(ov => ov.id === p.id);
            return dragOv ? { ...p, x: dragOv.x, y: dragOv.y } : p;
        });
        const frameBall = ballOverwrite || liveBall;
        return { players: framePlayers, ball: frameBall };
    };

    const currentFrame = getCurrentFrameState();

    const applyEditsToKeyframe = useRef<any>(null);
    applyEditsToKeyframe.current = () => {
        const entities: Record<string, { x: number, y: number, attachedTo?: string | null }> = {};

        currentFrame.players.forEach(p => {
            entities[p.id] = { x: p.x, y: p.y };
        });

        entities['ball'] = { x: currentFrame.ball.x, y: currentFrame.ball.y, attachedTo: currentFrame.ball.attachedTo };

        dispatch(addKeyframe({
            time: currentTime,
            entities
        }));
    };

    const handlePlayerDrag = (e: KonvaEventObject<DragEvent>, player: Player) => {
        const shape = e.target;

        let newOverwrites = dragOverwrites.filter(o => o.id !== player.id);
        newOverwrites.push({ id: player.id, x: shape.x(), y: shape.y() });
        setDragOverwrites(newOverwrites);

        if (currentFrame.ball.attachedTo === player.id) {
            setBallOverwrite({
                x: shape.x() + playerRadius,
                y: shape.y() + playerRadius,
                attachedTo: player.id
            });
        }
    };

    const handlePlayerDragEnd = (e: KonvaEventObject<DragEvent>, player: Player) => {
        handlePlayerDrag(e, player);
        applyEditsToKeyframe.current();
    };

    const handleBallDragStart = (e: KonvaEventObject<DragEvent>) => {
        setBallOverwrite({ x: e.target.x(), y: e.target.y(), attachedTo: null });
    };

    const handleBallDragEnd = (e: KonvaEventObject<DragEvent>) => {
        const ballShape = e.target;
        const newX = ballShape.x();
        const newY = ballShape.y();

        let attachedTo = null;
        let snapX = newX;
        let snapY = newY;

        for (const player of currentFrame.players) {
            const dist = Math.sqrt(Math.pow(player.x - newX, 2) + Math.pow(player.y - newY, 2));
            if (dist <= playerRadius) { // player radius
                attachedTo = player.id;
                snapX = player.x + playerRadius; // offset
                snapY = player.y + playerRadius;
                break;
            }
        }

        setBallOverwrite({ x: snapX, y: snapY, attachedTo });
        // Required for React state closure update cycle
        setTimeout(() => applyEditsToKeyframe.current(), 10);
    };

    const deselectPlayer = (e: KonvaEventObject<MouseEvent | TouchEvent>) => {
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            dispatch(setSelectedPlayerId(null));
        }
    };

    useEffect(() => {
        const handleExportVideo = async () => {
            if (!stageRef.current) return;

            const encoder = await HME.createH264MP4Encoder();
            encoder.width = COURT_WIDTH * 2;
            encoder.height = COURT_HEIGHT * 2;
            encoder.frameRate = 30;
            encoder.kbps = 8000;
            encoder.quantizationParameter = 10;
            encoder.initialize();

            dispatch(setCurrentTime(0));
            dispatch(setSelectedPlayerId(null));

            const totalDuration = 5000; // Assuming 5 seconds for the demo, replace with actual duration
            const frameDelay = 1000 / 30; // 30 FPS

            for (let time = 0; time < totalDuration; time += frameDelay) {
                dispatch(setCurrentTime(time));
                await new Promise(r => setTimeout(r, 10)); // wait for render

                const canvas = stageRef.current.toCanvas({ pixelRatio: 2 });
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    const imageData = ctx.getImageData(0, 0, COURT_WIDTH * 2, COURT_HEIGHT * 2);
                    encoder.addFrameRgba(imageData.data);
                }
            }

            encoder.finalize();
            const uint8Array = encoder.FS.readFile(encoder.outputFilename);
            const blob = new Blob([uint8Array], { type: 'video/mp4' });
            saveAs(blob, 'export.mp4');
            encoder.delete();
        };

        const handleExportZip = async () => {
             const zip = new JSZip();
             if (!stageRef.current) return;

             // Extract some sample keyframes intervals
             dispatch(setSelectedPlayerId(null));
             for (let t = 0; t <= 5000; t += 1000) {
                 dispatch(setCurrentTime(t));
                 await new Promise(r => setTimeout(r, 100)); // wait for render
                 const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2 });
                 const data = dataUrl.split(',')[1];
                 zip.file(`frame-${t}ms.png`, data, { base64: true });
             }

             zip.generateAsync({ type: 'blob' }).then(content => {
                 saveAs(content, 'frames.zip');
             });
        };

        triggerExportEvent.addEventListener('exportVideo', handleExportVideo);
        triggerExportEvent.addEventListener('exportZip', handleExportZip);
        return () => {
            triggerExportEvent.removeEventListener('exportVideo', handleExportVideo);
            triggerExportEvent.removeEventListener('exportZip', handleExportZip);
        };
    }, [dispatch]);

    useEffect(() => {
        const handleCaptureFrame = () => applyEditsToKeyframe.current();
        triggerExportEvent.addEventListener('captureFrame', handleCaptureFrame);
        return () => triggerExportEvent.removeEventListener('captureFrame', handleCaptureFrame);
    });

    return (
        <div ref={containerRef} className="flex-1 bg-gray-100 flex justify-center items-center p-2 md:p-4 relative touch-none">
            <Stage ref={stageRef} width={COURT_WIDTH * stageScale} height={COURT_HEIGHT * stageScale} scaleX={stageScale} scaleY={stageScale} className="bg-green-700 shadow-xl border-4 border-white"
                   onMouseDown={deselectPlayer}
                   onTouchStart={deselectPlayer}
            >
                <Layer>
                    <Rect width={COURT_WIDTH} height={COURT_HEIGHT} fill="#15803d" name="bg" />
                    {bgUrl ? <BackgroundImage url={bgUrl} /> : (
                        <Group name="bg">
                            <Rect width={COURT_WIDTH} height={COURT_HEIGHT} stroke="white" strokeWidth={4} name="bg" />
                            <Line points={[COURT_WIDTH/2, 0, COURT_WIDTH/2, COURT_HEIGHT]} stroke="white" strokeWidth={2} />
                            <Circle x={COURT_WIDTH/2} y={COURT_HEIGHT/2} radius={75} stroke="white" strokeWidth={2} />
                            <Line points={[COURT_WIDTH/3, 0, COURT_WIDTH/3, COURT_HEIGHT]} stroke="white" strokeWidth={2} dash={[10, 5]} />
                            <Line points={[2*COURT_WIDTH/3, 0, 2*COURT_WIDTH/3, COURT_HEIGHT]} stroke="white" strokeWidth={2} dash={[10, 5]} />
                            <Circle x={0} y={COURT_HEIGHT/2} radius={120} stroke="white" strokeWidth={2} />
                            <Circle x={COURT_WIDTH} y={COURT_HEIGHT/2} radius={120} stroke="white" strokeWidth={2} />
                        </Group>
                    )}
                </Layer>
                <Layer>
                    {currentFrame.players.map((p) => {
                        const isSelected = p.id === selectedPlayerId;
                        return (
                            <Group
                                key={p.id}
                                x={p.x}
                                y={p.y}
                                draggable
                                onDragMove={(e) => handlePlayerDrag(e, p)}
                                onDragEnd={(e) => handlePlayerDragEnd(e, p)}
                                onClick={() => dispatch(setSelectedPlayerId(p.id))}
                                onTap={() => dispatch(setSelectedPlayerId(p.id))}
                            >
                                {p.shape === 'circle' ? (
                                    <Circle radius={playerRadius} fill={p.color} stroke={isSelected ? "yellow" : "black"} strokeWidth={isSelected ? 3 : 1} shadowBlur={isSelected ? 10 : 0} shadowColor="black" />
                                ) : (
                                    <RegularPolygon sides={3} radius={playerRadius} fill={p.color} stroke={isSelected ? "yellow" : "black"} strokeWidth={isSelected ? 3 : 1} shadowBlur={isSelected ? 10 : 0} shadowColor="black" />
                                )}
                                <Text text={p.label || ""} fontSize={isMobile ? 30 : 12} fill="white" x={isMobile ? -25 : -10} y={isMobile ? -12 : -5} width={isMobile ? 50 : 20} align="center" />
                            </Group>
                        );
                    })}
                    <Circle
                        x={currentFrame.ball.x}
                        y={currentFrame.ball.y}
                        radius={ballRadius}
                        fill="yellow"
                        stroke="black"
                        strokeWidth={1}
                        draggable
                        onDragStart={handleBallDragStart}
                        onDragEnd={handleBallDragEnd}
                    />
                </Layer>
            </Stage>
        </div>
    );
};
